import { useState } from 'react';
import {
  HelpCircle,
  X,
  Package,
  Box,
  Layers,
  ChefHat,
  BookOpen,
  ArrowRight,
  ShoppingCart,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export function CulinaryHelpPanel() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="gap-2"
      >
        <HelpCircle className="h-4 w-4" />
        How It Works
      </Button>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-4xl max-h-[90vh] flex flex-col rounded-lg bg-background shadow-xl">
            {/* Header */}
            <div className="flex-shrink-0 flex items-start justify-between border-b px-6 py-4">
              <div className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-blue-600" />
                <h3 className="text-xl font-semibold">Understanding the Culinary Hierarchy</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-full p-2 hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 min-h-[400px]">
              <div className="space-y-6">
                <HierarchyDiagram />
                <LevelExplanations />
                <ExampleRecipe />
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 flex justify-end border-t px-6 py-4">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function HierarchyDiagram() {
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 py-3 text-sm">
      <div className="flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1">
        <Package className="h-4 w-4 text-gray-600" />
        <span>Catalog Items</span>
      </div>
      <ArrowRight className="h-4 w-4 text-muted-foreground" />
      <div className="flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1">
        <Box className="h-4 w-4 text-gray-600" />
        <span>Components</span>
      </div>
      <ArrowRight className="h-4 w-4 text-muted-foreground" />
      <div className="flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1">
        <Layers className="h-4 w-4 text-purple-600" />
        <span>Sub-Recipes</span>
      </div>
      <ArrowRight className="h-4 w-4 text-muted-foreground" />
      <div className="flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1">
        <ChefHat className="h-4 w-4 text-blue-600" />
        <span>Menu Items</span>
      </div>
      <ArrowRight className="h-4 w-4 text-muted-foreground" />
      <div className="flex items-center gap-1 rounded-full bg-green-100 px-3 py-1">
        <BookOpen className="h-4 w-4 text-green-600" />
        <span>Menus</span>
      </div>
    </div>
  );
}

function LevelExplanations() {
  const levels = [
    {
      icon: Package,
      color: 'text-gray-600',
      bg: 'bg-gray-100',
      title: 'Catalog Items',
      description: 'Raw items you purchase from suppliers. These are SKUs in your inventory.',
      examples: 'Tomatoes, Olive Oil, Chicken Breast, Ketchup Sachets, Napkins, Plastic Forks',
      link: '/catalog/items',
    },
    {
      icon: Box,
      color: 'text-gray-600',
      bg: 'bg-gray-100',
      title: 'Components',
      description: 'Simple preparations with minimal steps. Building blocks for other recipes.',
      examples: 'Compound Herb Butter, House Spice Blend, Garlic Oil, Clarified Butter',
    },
    {
      icon: Layers,
      color: 'text-purple-600',
      bg: 'bg-purple-100',
      title: 'Sub-Recipes',
      description: 'Reusable preparations used across multiple dishes.',
      examples: 'Béarnaise Sauce, Chicken Stock, Marinara, Pesto, Hollandaise',
    },
    {
      icon: ChefHat,
      color: 'text-blue-600',
      bg: 'bg-blue-100',
      title: 'Menu Items',
      description: 'Finished dishes served to customers. These appear on menus and drive demand forecasting.',
      examples: 'Grilled Salmon with Béarnaise, Caesar Salad, Tiramisu, Burger Meal',
    },
  ];

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {levels.map((level) => {
        const Icon = level.icon;
        return (
          <div key={level.title} className="rounded-lg border bg-white p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className={`rounded-full p-1 ${level.bg}`}>
                <Icon className={`h-4 w-4 ${level.color}`} />
              </div>
              <span className="font-medium text-sm">{level.title}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-2">{level.description}</p>
            <p className="text-xs">
              <span className="text-muted-foreground">Examples: </span>
              <span className="text-foreground">{level.examples}</span>
            </p>
          </div>
        );
      })}
    </div>
  );
}

function ExampleRecipe() {
  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="flex items-center gap-2 mb-3">
        <ShoppingCart className="h-4 w-4 text-green-600" />
        <span className="font-medium text-sm">Example: Burger Meal Menu Item</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        When you forecast 500 Burger Meals for tomorrow, the system calculates all items needed:
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b">
              <th className="text-left py-1 pr-4 font-medium">Ingredient</th>
              <th className="text-left py-1 pr-4 font-medium">Type</th>
              <th className="text-right py-1 font-medium">Per Meal</th>
              <th className="text-right py-1 font-medium">× 500</th>
            </tr>
          </thead>
          <tbody className="text-muted-foreground">
            <tr className="border-b border-dashed">
              <td className="py-1 pr-4">Beef Patty 6oz</td>
              <td className="py-1 pr-4">
                <span className="rounded bg-gray-100 px-1">Catalog</span>
              </td>
              <td className="text-right py-1 pr-4">1</td>
              <td className="text-right py-1 font-medium text-foreground">500</td>
            </tr>
            <tr className="border-b border-dashed">
              <td className="py-1 pr-4">Burger Bun</td>
              <td className="py-1 pr-4">
                <span className="rounded bg-gray-100 px-1">Catalog</span>
              </td>
              <td className="text-right py-1 pr-4">1</td>
              <td className="text-right py-1 font-medium text-foreground">500</td>
            </tr>
            <tr className="border-b border-dashed">
              <td className="py-1 pr-4">House Sauce</td>
              <td className="py-1 pr-4">
                <span className="rounded bg-purple-100 px-1">Sub-Recipe</span>
              </td>
              <td className="text-right py-1 pr-4">30ml</td>
              <td className="text-right py-1 font-medium text-foreground">15L</td>
            </tr>
            <tr className="border-b border-dashed">
              <td className="py-1 pr-4">Ketchup Sachet</td>
              <td className="py-1 pr-4">
                <span className="rounded bg-gray-100 px-1">Catalog</span>
              </td>
              <td className="text-right py-1 pr-4">2</td>
              <td className="text-right py-1 font-medium text-foreground">1,000</td>
            </tr>
            <tr className="border-b border-dashed">
              <td className="py-1 pr-4">Napkin</td>
              <td className="py-1 pr-4">
                <span className="rounded bg-gray-100 px-1">Catalog</span>
              </td>
              <td className="text-right py-1 pr-4">3</td>
              <td className="text-right py-1 font-medium text-foreground">1,500</td>
            </tr>
            <tr>
              <td className="py-1 pr-4">Plastic Fork</td>
              <td className="py-1 pr-4">
                <span className="rounded bg-gray-100 px-1">Catalog</span>
              </td>
              <td className="text-right py-1 pr-4">1</td>
              <td className="text-right py-1 font-medium text-foreground">500</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground mt-3 italic">
        This is how demand forecasting works — Menu Items drive the calculation of what to order.
      </p>
    </div>
  );
}
